/**
 * CustomerForm component for creating a new WooCommerce customer inline.
 *
 * Renders a compact form suitable for embedding inside the CustomerSearch
 * modal. Validates required fields (first name, email format) before
 * submitting to the SymplePOS REST API.
 *
 * @param {Object}   props
 * @param {Function} props.onCreated - Callback fired with the newly created customer object on success.
 * @param {Function} props.onCancel  - Callback fired when the user cancels form entry.
 */
import { useState, useCallback, createElement } from 'react';
import api from '../../api/client';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CustomerForm({ onCreated, onCancel }) {
  const [fields, setFields] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /**
   * Update a single field value and clear its validation error.
   */
  const handleChange = useCallback((field) => (e) => {
    const value = e.target.value;
    setFields((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (apiError) setApiError('');
  }, [apiError]);

  /**
   * Validate all fields and return true if the form is valid.
   */
  const validate = useCallback(() => {
    const next = {};

    if (!fields.first_name.trim()) {
      next.first_name = 'First name is required.';
    }

    if (!fields.email.trim()) {
      next.email = 'Email is required.';
    } else if (!EMAIL_REGEX.test(fields.email.trim())) {
      next.email = 'Please enter a valid email address.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }, [fields]);

  /**
   * Submit the form to the API.
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setApiError('');

    try {
      const customer = await api.createCustomer({
        first_name: fields.first_name.trim(),
        last_name: fields.last_name.trim(),
        email: fields.email.trim(),
        phone: fields.phone.trim(),
      });

      if (typeof onCreated === 'function') {
        onCreated(customer);
      }
    } catch (err) {
      const message =
        err?.message ||
        err?.data?.message ||
        'Failed to create customer. Please try again.';
      setApiError(message);
    } finally {
      setSubmitting(false);
    }
  }, [fields, validate, onCreated]);

  const inputClasses =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 ' +
    'placeholder-gray-400 outline-none transition-shadow ' +
    'focus:border-pos-primary focus:ring-2 focus:ring-pos-primary/20';

  const errorInputClasses =
    'block w-full rounded-md border border-red-400 bg-white px-3 py-2 text-sm text-gray-900 ' +
    'placeholder-gray-400 outline-none transition-shadow ' +
    'focus:border-red-500 focus:ring-2 focus:ring-red-200';

  return (
    <form onSubmit={handleSubmit} className="px-4 py-4 space-y-3" noValidate>
      {/* ---- Header ---- */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">New Customer</h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>

      {/* ---- API error banner ---- */}
      {apiError && (
        <div
          className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          <svg
            className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{apiError}</span>
        </div>
      )}

      {/* ---- Name fields (side by side) ---- */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="symple-pos-cf-first-name" className="block text-xs font-medium text-gray-700 mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            id="symple-pos-cf-first-name"
            type="text"
            value={fields.first_name}
            onChange={handleChange('first_name')}
            placeholder="Jane"
            className={errors.first_name ? errorInputClasses : inputClasses}
            autoComplete="given-name"
          />
          {errors.first_name && (
            <p className="mt-1 text-xs text-red-600">{errors.first_name}</p>
          )}
        </div>

        <div>
          <label htmlFor="symple-pos-cf-last-name" className="block text-xs font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            id="symple-pos-cf-last-name"
            type="text"
            value={fields.last_name}
            onChange={handleChange('last_name')}
            placeholder="Doe"
            className={inputClasses}
            autoComplete="family-name"
          />
        </div>
      </div>

      {/* ---- Email ---- */}
      <div>
        <label htmlFor="symple-pos-cf-email" className="block text-xs font-medium text-gray-700 mb-1">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          id="symple-pos-cf-email"
          type="email"
          value={fields.email}
          onChange={handleChange('email')}
          placeholder="jane@example.com"
          className={errors.email ? errorInputClasses : inputClasses}
          autoComplete="email"
        />
        {errors.email && (
          <p className="mt-1 text-xs text-red-600">{errors.email}</p>
        )}
      </div>

      {/* ---- Phone ---- */}
      <div>
        <label htmlFor="symple-pos-cf-phone" className="block text-xs font-medium text-gray-700 mb-1">
          Phone
        </label>
        <input
          id="symple-pos-cf-phone"
          type="tel"
          value={fields.phone}
          onChange={handleChange('phone')}
          placeholder="+1 (555) 123-4567"
          className={inputClasses}
          autoComplete="tel"
        />
      </div>

      {/* ---- Submit ---- */}
      <button
        type="submit"
        disabled={submitting}
        className={
          'w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold ' +
          'text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ' +
          (submitting
            ? 'bg-pos-primary/70 cursor-not-allowed focus:ring-pos-primary/40'
            : 'bg-pos-primary hover:bg-pos-primary-dark focus:ring-pos-primary')
        }
      >
        {submitting && (
          <svg
            className="h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        {submitting ? 'Creating...' : 'Create Customer'}
      </button>
    </form>
  );
}
